import * as _ from 'underscore';
import { getAppStore } from '../../app/AppStore';
import { RequestHelper } from '../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { Module } from '../Module';
import {
  CreateOrUpdateSiteOIDCConfigurationRequest,
} from '../ServerApi/requests/CreateOrUpdateSiteOIDCConfigurationRequest';
import {
  GetOrCreateSiteOIDCConfigurationViewModelRequest,
  ISiteOIDCConfigurationViewModel,
} from '../ServerApi/requests/GetOrCreateSiteOIDCConfigurationViewModelRequest';
import { GetSiteSAMLConfigurationRequest } from '../ServerApi/requests/GetSiteSAMLConfigurationRequest';
import { RemoveIdentityProviderRequest } from '../ServerApi/requests/RemoveIdentityProviderRequest';
import { SetSiteAttributeMappingRequest } from '../ServerApi/requests/SetSiteAttributeMapping';
import {
  SetSiteSAMLAllowEmbeddedAuthenticationRequest,
} from '../ServerApi/requests/SetSiteSAMLAllowEmbeddedAuthenticationRequest';
import {
  SetSiteSAMLAuthSettingForEmbeddedViewsRequest,
} from '../ServerApi/requests/SetSiteSAMLAuthSettingForEmbeddedViewsRequest';
import { SetSiteSAMLEnabledRequest } from '../ServerApi/requests/SetSiteSAMLEnabledRequest';
import { UpdateUserAuthSettingsRequest } from '../ServerApi/requests/UpdateUserAuthSettingsRequest';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ISiteSAMLConfiguration } from '../ServerApi/types/ISiteSAMLConfiguration';
import { SiteUserAuthSetting } from '../ServerApi/types/ISiteUser';
import {
  getSessionManagementServiceInstance,
  SiteDefaultAuth,
} from '../../sessionManagement/SessionManagementService';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import WebHttpClient from '../../httpClient/WebHttpClient';
import {getAuthenticationServiceInstance} from '../../authentication/services/AuthenticationService';
import { ISiteUserInfo } from '../models/ISiteUserInfo';

export interface IAuthenticationAttributeMapping {
  emailMapping: string;
  firstNameMapping: string;
  lastNameMapping: string;
  fullNameMapping: string;
  useFullName: boolean;
}

export interface IAuthenticationProvider {
  allowEmbeddedAuthentication: boolean;
  id: string;
  isEditable: boolean;
  isEnabled: boolean;
  isLogoutSupported: boolean;
  testLoginUrl: string;
  type: string;
}

export const AuthenticationProviderType = {
  default: 'DEFAULT',
  oidc: 'OIDC',
  saml: 'SAML',
  tabidmfa: 'TABID_WITH_MFA',
};

export interface IOIDCAuthenticationProvider extends IAuthenticationProvider {
  accessTokenUri: string;
  alias: string;
  clientId: string;
  clientSecret: string;
  userAuthorizationUri: string;
  userInfoUri: string;
}

export interface ISAMLAuthenticationProvider extends IAuthenticationProvider {
  acsUrl: string;
  attributeMapping: IAuthenticationAttributeMapping;
  certificateUrl: string;
  identityProviderEntityId: string;
  serviceProviderEntityId: string;
  ssoServiceURL: string;
}

export interface ISiteAuthenticationSettings {
  defaultProvider: IAuthenticationProvider;
  providerIdForEmbeddedViews: string; // null means: "let the user decide which provider to use"
  externalProviders: IAuthenticationProvider[];
  tabIdMfaProvider: IAuthenticationProvider;

}

interface ISiteAuthenticationSettingsInternal extends ISiteAuthenticationSettings {
  samlConfiguration: ISiteSAMLConfiguration;
  oidcViewModel: ISiteOIDCConfigurationViewModel;
}

const DefaultAuthenticationProviderId = 'DEFAULT';
export const DefaultAuthenticationProvider: IAuthenticationProvider = {
  allowEmbeddedAuthentication: false,
  id: DefaultAuthenticationProviderId,
  isEditable: false,
  isEnabled: true,
  isLogoutSupported: true,
  testLoginUrl: null,
  type: AuthenticationProviderType.default,
};
const TabIdWithMFAAuthenticationProviderId = 'TABID_WITH_MFA';
export const TabIdWithMFAAuthenticationProvider: IAuthenticationProvider = {
  allowEmbeddedAuthentication: false,
  id: TabIdWithMFAAuthenticationProviderId,
  isEditable: false,
  isEnabled: false,
  isLogoutSupported: true,
  testLoginUrl: null,
  type: AuthenticationProviderType.tabidmfa,
};

export const AuthenticationSettingsWhenDisabled: ISiteAuthenticationSettings = {
  defaultProvider: DefaultAuthenticationProvider,
  providerIdForEmbeddedViews: null,
  externalProviders: [],
  tabIdMfaProvider: null,

};

export function is3rdParty(authenticationProviderId: string): boolean{
  return authenticationProviderId !== DefaultAuthenticationProviderId && authenticationProviderId !== TabIdWithMFAAuthenticationProviderId;
}

// shim needed to translate between the new SiteAuthenticationSettings client API above and the existing SAML/OIDC server APIs
export class SiteAuthenticationSettingsService {

  private boundOnAppStateChanged = this.onAppStateChanged.bind(this);
  private siteId: string;
  private siteAuthenticationSettingsInternalPromise: ng.IPromise<ISiteAuthenticationSettingsInternal>;

  public static $inject = [
    '$q',
    '$translate'
  ];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService
  ) {
    const unsubscribe = getAppStore().subscribe(this.boundOnAppStateChanged);
  }

  public isEnabled(): boolean {
    return ServerSettingsUnauthenticated.isSiteSamlEnabled();
  }

  public getSiteAuthenticationSettings(refresh: boolean = false): ng.IPromise<ISiteAuthenticationSettings> {
    if (this.isEnabled()) {
      return this.getSiteAuthenticationSettingsInternal(refresh);
    } else {
      return this.$q.resolve(AuthenticationSettingsWhenDisabled);
    }
  }

  public getDefaultProvider(): IAuthenticationProvider {
    return DefaultAuthenticationProvider;
  }
  public getTabIdMFAProvider(): IAuthenticationProvider {
    return TabIdWithMFAAuthenticationProvider;
  }

  public enableProvider(id: string): ng.IPromise<void> {
    this.throwUnlessEnabled();
    return this.getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      const newSettings = angular.copy(settings);
      const provider = this.findProvider(newSettings, id);
      let noChanges = true;

      _.each(newSettings.externalProviders, (provider: IAuthenticationProvider) => {
        const shouldBeEnabled = (provider.id === id);
        noChanges = noChanges && (provider.isEnabled === shouldBeEnabled);
        provider.isEnabled = shouldBeEnabled;
      });
      if (noChanges) {
        return Promise.resolve();
      }
      switch(provider.type) {
        case AuthenticationProviderType.saml:
          newSettings.samlConfiguration.enabled = true;
          newSettings.oidcViewModel.configuration.enabled = false;
          break;
        case AuthenticationProviderType.oidc:
          newSettings.samlConfiguration.enabled = false;
          newSettings.oidcViewModel.configuration.enabled = true;
          newSettings.oidcViewModel.configuration.knownProviderAlias = (<IOIDCAuthenticationProvider>provider).alias;
          break;
        case AuthenticationProviderType.default:
          newSettings.samlConfiguration.enabled = false;
          newSettings.oidcViewModel.configuration.enabled = false;
          break;
        default:
          throw new Error(`Unsupported provider type ${provider.type}`);
      }
      return Promise.all([
        WebHttpClient.sendRequest(new SetSiteSAMLEnabledRequest({ enable: newSettings.samlConfiguration.enabled})),
        WebHttpClient.sendRequest(new CreateOrUpdateSiteOIDCConfigurationRequest({ siteOIDCConfiguration: newSettings.oidcViewModel.configuration }))
      ]).then(() => {
        angular.copy(newSettings, settings);
      });
    });
  }

  // angular.copy is used so that $watchers may observe the change
  private handleSAMLConfigurationUpdate(samlConfiguration: ISiteSAMLConfiguration): ng.IPromise<void> {
    return this.getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
      angular.copy(samlConfiguration, settings.samlConfiguration);
      const updatedProvider = this.getSAMLProvider(samlConfiguration);
      const provider = this.findProvider(settings, updatedProvider.id);
      angular.copy(updatedProvider, provider);
    });
  }

  public removeSAMLMetadata(id: string): Promise<void> {
    this.throwUnlessEnabled();
    return WebHttpClient.sendRequest(new RemoveIdentityProviderRequest()).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return this.handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  public uploadSAMLMetadata(id: string, samlMetadata: File): ng.IPromise<void> {
    this.throwUnlessEnabled();
    return getSessionManagementServiceInstance().uploadSAMLMetadata(samlMetadata).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return this.handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  public setSAMLAttributeMapping(id: string, attributeMapping: IAuthenticationAttributeMapping): Promise<void> {
    this.throwUnlessEnabled();
    return WebHttpClient.sendRequest(new SetSiteAttributeMappingRequest(attributeMapping)).then(() => {
      return WebHttpClient.sendRequest(new GetSiteSAMLConfigurationRequest());
    }).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return this.handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  // should be removed by TFSID:607630
  public getAuthSetting(id: string, settings: ISiteAuthenticationSettings): string {
    const provider = this.findProvider(settings, id);
    if (!provider) {
      throw new Error(`Provider with id ${id} doesn't exist`);
    }
    switch (provider.type) {
      case AuthenticationProviderType.saml:
        return SiteUserAuthSetting.Saml;
      case AuthenticationProviderType.oidc:
        return SiteUserAuthSetting.Oidc;
      case AuthenticationProviderType.default:
        return SiteUserAuthSetting.Default;
      case AuthenticationProviderType.tabidmfa:
        return SiteUserAuthSetting.TabId_with_MFA;
      default:
        throw new Error(`Unsupported provider type ${provider.type}`);
    }
  }

  public setProviderIdForEmbeddedViews(id: string): ng.IPromise<void> {
    this.throwUnlessEnabled();
    return this.getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = (id ? this.getAuthSetting(id, settings) : null);
      return WebHttpClient.sendRequest(new SetSiteSAMLAuthSettingForEmbeddedViewsRequest({authSetting: authSetting})).then(() => {
        settings.providerIdForEmbeddedViews = id;
      });
    });
  }

  public setProviderIdForUsers(id: string, userIds: string[]): ng.IPromise<IResult> {
    this.throwUnlessEnabled();
    return this.getSiteAuthenticationSettings().then((settings: ISiteAuthenticationSettings) => {
      const authSetting = this.getAuthSetting(id, settings);
      const requests = RequestHelper.buildRequests(userIds, (userIds => new UpdateUserAuthSettingsRequest(userIds, authSetting)));
      return WebHttpClient.sendRequestsParallel(requests, ResultHelper.combineResults);
    });
  }

  public setAllowEmbeddedAuthentication(id: string, val: boolean): Promise<void> {
    this.throwUnlessEnabled();
    return WebHttpClient.sendRequest(new SetSiteSAMLAllowEmbeddedAuthenticationRequest({allow: val})).then((samlConfiguration: ISiteSAMLConfiguration) => {
      return this.handleSAMLConfigurationUpdate(samlConfiguration);
    });
  }

  public getProviderDisplayName(provider: IAuthenticationProvider): string {
    switch (provider.type) {
      case AuthenticationProviderType.oidc:
        return this.getDisplayNameForOIDCProvider(<IOIDCAuthenticationProvider>provider);
      case AuthenticationProviderType.saml:
        return this.getDisplayNameForSAMLProvider(<ISAMLAuthenticationProvider>provider);
      case AuthenticationProviderType.default:
        return this.getDisplayNameForDefaultProvider(provider);
      case AuthenticationProviderType.tabidmfa:
        return this.getDisplayNameForTOLWithMFAProvider();
      default:
        throw new Error(`Unsupported provider type ${provider.type}`);
    }
  }
  getDisplayNameForTOLWithMFAProvider(): string {
    return this.$translate.instant('ssoAuthTypeDefault_name_tableauid_mfa');
  }

  public findProvider(settings: ISiteAuthenticationSettings, id: string) {
    if (id === settings.defaultProvider.id) {
      return settings.defaultProvider;
    }
    if (id === settings.tabIdMfaProvider.id) {
      return settings.tabIdMfaProvider;
    }
    return _.find(settings.externalProviders, (provider: IAuthenticationProvider) => (provider.id === id));
  }

  public isAuthenticationSSO(settings: ISiteAuthenticationSettings) {
    return settings && settings.externalProviders && !!_.find(settings.externalProviders, (provider: IAuthenticationProvider) => (provider.isEnabled));
  }

  // should be removed by TFSID:607630
  public getAuthSettingToProviderIdMap(): ng.IPromise<_.Dictionary<string>> {
    if (this.isEnabled()) {
      return this.getSiteAuthenticationSettingsInternal().then((settings: ISiteAuthenticationSettingsInternal) => {
        return this.getAuthSettingToProviderIdMapInternal(settings.oidcViewModel);
      });
    } else {
      const map = {
        [SiteUserAuthSetting.Default]: DefaultAuthenticationProvider.id
      };
      return this.$q.resolve(map);
    }
  }

  private throwUnlessEnabled() {
    if (!this.isEnabled()) {
      throw new Error('Site authentication settings are not enabled');
    }
  }

  private getSiteAuthenticationSettingsInternal(refresh: boolean = false): ng.IPromise<ISiteAuthenticationSettingsInternal> {
    if (!this.siteAuthenticationSettingsInternalPromise || refresh) {
      this.siteAuthenticationSettingsInternalPromise = this.getSiteAuthenticationSettingsInternalImpl();
    }
    return this.siteAuthenticationSettingsInternalPromise;
  }

  private getAuthSettingToProviderIdMapInternal(oidcViewModel: ISiteOIDCConfigurationViewModel): _.Dictionary<string> {
    // keys: siteUserAuthSetting, values: authentication provider id
    const map = {
      [SiteUserAuthSetting.Oidc]: this.getOIDCProviderId(oidcViewModel.configuration.knownProviderAlias),
      [SiteUserAuthSetting.Saml]: this.getSAMLProviderId(),
      [SiteUserAuthSetting.Default]: DefaultAuthenticationProvider.id,
      [SiteUserAuthSetting.TabId_with_MFA]: TabIdWithMFAAuthenticationProvider.id
    };
    return map;
  }

  private getSiteAuthenticationSettingsInternalImpl(): Promise<ISiteAuthenticationSettingsInternal> {
    return Promise.all([
      WebHttpClient.sendRequest(new GetSiteSAMLConfigurationRequest()).catch(() => {
        /* Don't fail if the user has no permission */
        return {};
      }),
      WebHttpClient.sendRequest(new GetOrCreateSiteOIDCConfigurationViewModelRequest())
    ]).then((results: any[]) => {
      const samlConfiguration: ISiteSAMLConfiguration = results[0];
      const oidcViewModel: ISiteOIDCConfigurationViewModel = results[1];

      const externalProviders: IAuthenticationProvider[] = this.getBuiltinOIDCProviders(oidcViewModel);
      const samlProvider: ISAMLAuthenticationProvider = this.getSAMLProvider(samlConfiguration);
      externalProviders.push(samlProvider);
      const tabIdMfaProvider = TabIdWithMFAAuthenticationProvider;
      if(getSessionManagementServiceInstance().isMfaEnabled()){
        tabIdMfaProvider.isEnabled = true;
      }
      else{
        tabIdMfaProvider.isEnabled = false;
      }
      const authSettingToProviderIdMap = this.getAuthSettingToProviderIdMapInternal(oidcViewModel);

      return {
        defaultProvider: DefaultAuthenticationProvider,
        externalProviders: externalProviders,
        tabIdMfaProvider: tabIdMfaProvider,
        providerIdForEmbeddedViews: authSettingToProviderIdMap[samlConfiguration.authSettingForEmbeddedViews],
        samlConfiguration: samlConfiguration,
        oidcViewModel: oidcViewModel,
      };
    });
  }

  private getOIDCProviderId(alias: string) {
    return AuthenticationProviderType.oidc + ':' + alias;
  }

  private getBuiltinOIDCProviders(oidcViewModel: ISiteOIDCConfigurationViewModel): IOIDCAuthenticationProvider[] {
    const oidcConfiguration = oidcViewModel.configuration;
    return _.map(oidcViewModel.knownProviderAliases, (knownProviderAlias: string) => {
      return {
        allowEmbeddedAuthentication: false,
        id: this.getOIDCProviderId(knownProviderAlias),
        isEditable: false,
        isEnabled: oidcConfiguration.enabled && oidcConfiguration.knownProviderAlias === knownProviderAlias,
        isLogoutSupported: true,
        testLoginUrl: oidcConfiguration.testLoginUrl,
        type: AuthenticationProviderType.oidc,

        accessTokenUri: null,
        alias: knownProviderAlias,
        clientId: null,
        clientSecret: null,
        userAuthorizationUri: null,
        userInfoUri: null,
      };
    });
  }

  private getSAMLProviderId() {
    return AuthenticationProviderType.saml;
  }

  private getSAMLProvider(samlConfiguration: ISiteSAMLConfiguration): ISAMLAuthenticationProvider {
    return {
      allowEmbeddedAuthentication: samlConfiguration.allowEmbeddedAuthentication,
      id: this.getSAMLProviderId(),
      isEditable: true,
      isEnabled: samlConfiguration.enabled,
      isLogoutSupported: samlConfiguration.isSingleLogoutSupported,
      testLoginUrl: samlConfiguration.testLoginUrl,
      type: AuthenticationProviderType.saml,

      acsUrl: samlConfiguration.acsUrl,
      attributeMapping: samlConfiguration.siteSAMLAttributeMapping,
      certificateUrl: samlConfiguration.certificateUrl,
      identityProviderEntityId: samlConfiguration.identityProviderEntityId,
      serviceProviderEntityId: samlConfiguration.serviceProviderEntityId,
      ssoServiceURL: samlConfiguration.ssoServiceURL,
    };
  }

  private getDisplayNameForSAMLProvider(provider: ISAMLAuthenticationProvider): string {
    const providerName = this.maybeGetHost(provider.ssoServiceURL);
    const translateKey = providerName ? 'ssoAuthTypeSAML_name' : 'ssoAuthTypeSAML_name_undefined';
    const translateValues = { PROVIDER_NAME: providerName };
    return this.$translate.instant(translateKey, translateValues);
  }

  private getDisplayNameForOIDCProvider(provider: IOIDCAuthenticationProvider): string {
    const translateKey = provider.isEditable ? 'ssoAuthTypeOIDC_name_editable' : 'ssoAuthTypeOIDC_name';
    const translateValues = { PROVIDER_NAME: provider.alias || this.maybeGetHost(provider.userInfoUri) };
    return this.$translate.instant(translateKey, translateValues);
  }

  private getDisplayNameForDefaultProvider(provider: IAuthenticationProvider): string {
    let translateKey;
    if (ServerSettingsUnauthenticated.isTableauIdSupported()) {
      translateKey = 'ssoAuthTypeDefault_name_tableauid';
    } else {
      switch (getAuthenticationServiceInstance().siteAuthDefault()) {
        case SiteDefaultAuth[SiteDefaultAuth.Local]:
          translateKey = 'ssoAuthTypeDefault_name_local';
          break;
        case SiteDefaultAuth[SiteDefaultAuth.ActiveDirectory]:
          translateKey = 'ssoAuthTypeDefault_name_ad';
          break;
        case SiteDefaultAuth[SiteDefaultAuth.SAML]:
          translateKey = 'ssoAuthTypeDefault_name_saml';
          break;
        case SiteDefaultAuth[SiteDefaultAuth.External]:
          translateKey = 'ssoAuthTypeDefault_name_external';
          break;
        default:
          translateKey = 'ssoAuthTypeDefault_name_other';
          break;
      }
    }
    return this.$translate.instant(translateKey);
  }

  private maybeGetHost(urlString: string): string {
    const match = /\/\/.*?(\w+\.\w+)($|:|\/)/.exec(urlString);
    if (match) {
      return match[1];
    }
    return undefined;
  }

  private onAppStateChanged() {
    const state = getAppStore().getState();
    const siteId = state && state.session && state.session.info && state.session.info.site.id;
    if (this.siteId !== siteId) {
      this.siteId = siteId;
      this.siteAuthenticationSettingsInternalPromise = null;
    }
  }
}

Module.instance().service('SiteAuthenticationSettingsService', SiteAuthenticationSettingsService);
